import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiHelpCircle, FiCheck, FiAward, FiClock } from 'react-icons/fi';
import Toast from './Toast';
import CertificateModal from './CertificateModal';

import { API_URL } from '../config';

function TrainingQuiz() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [training, setTraining] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [trainingCompletion, setTrainingCompletion] = useState(null);
  const [awaitingGrading, setAwaitingGrading] = useState(false);

  useEffect(() => {
    loadQuiz();
  }, [id]);

  const [modulesIncomplete, setModulesIncomplete] = useState(false);

  const loadQuiz = async () => {
    try {
      // Load training to get quiz
      const trainingResponse = await axios.get(`${API_URL}/trainings/${id}`);
      if (trainingResponse.data.training) {
        setTraining(trainingResponse.data.training);
        if (trainingResponse.data.training.quiz) {
          setQuiz(trainingResponse.data.training.quiz);
        }
        setAwaitingGrading(!!trainingResponse.data.training.quizPendingReview);

        // Check if all modules are complete before allowing quiz
        const modules = trainingResponse.data.training.modules || [];
        if (modules.length > 0) {
          const progressResponse = await axios.get(`${API_URL}/training/progress/my`);
          const progress = progressResponse.data.progress || {};
          const allComplete = modules.every(m => progress[m.id]?.completed);
          if (!allComplete) {
            setModulesIncomplete(true);
          }
        }
      }
    } catch (error) {
      console.error('Error loading quiz:', error);
      setToast({ message: 'Error loading quiz', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const questionIsWriteIn = (q) => q.question_type === 'write_in';

  const handleAnswerChange = (questionId, answerIndex) => {
    if (!submitted) {
      setAnswers({
        ...answers,
        [questionId]: answerIndex,
      });
    }
  };

  const handleWriteInChange = (questionId, text) => {
    if (!submitted) {
      setAnswers({
        ...answers,
        [questionId]: text,
      });
    }
  };

  const isQuestionAnswered = (q) => {
    if (questionIsWriteIn(q)) {
      const t = answers[q.id];
      return typeof t === 'string' && t.trim().length > 0;
    }
    const v = answers[q.id];
    return v !== undefined && v !== null && v !== '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!quiz || !quiz.questions || quiz.questions.length === 0) {
      setToast({ message: 'No questions to submit', type: 'error' });
      return;
    }

    const unanswered = quiz.questions.filter((q) => !isQuestionAnswered(q));
    if (unanswered.length > 0) {
      setToast({
        message: `Please answer all questions. ${unanswered.length} question(s) remaining.`,
        type: 'error',
      });
      return;
    }

    const payload = {};
    quiz.questions.forEach((q) => {
      if (questionIsWriteIn(q)) {
        payload[q.id] = String(answers[q.id]).trim();
      } else {
        payload[q.id] = parseInt(answers[q.id], 10);
      }
    });

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/quizzes/${quiz.id}/submit`, { answers: payload });
      setResults(response.data);
      setSubmitted(true);

      if (response.data.pending_review) {
        setAwaitingGrading(true);
        setToast({
          message: 'Your answers were submitted. Written responses will be graded by an administrator.',
          type: 'success',
        });
        return;
      }

      if (response.data.passed) {
        setToast({ message: `Congratulations! You passed with ${response.data.score}%`, type: 'success' });
        
        // Check if this quiz pass triggered a training completion
        if (response.data.trainingCompleted && response.data.completion) {
          setTrainingCompletion(response.data.completion);
          // Show certificate after a brief delay so user sees the pass message first
          setTimeout(() => setShowCertificate(true), 1500);
        }
      } else {
        setToast({ message: `You scored ${response.data.score}%. Passing score is ${quiz.passing_score}%.`, type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setToast({ message: 'Error submitting quiz', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-600">Loading quiz...</div>
        </div>
      </div>
    );
  }

  if (!training || !quiz) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Quiz not found</h2>
          <button
            onClick={() => navigate(`/training/${id}`)}
            className="text-[#3c79bb] hover:text-primary-700"
          >
            Back to Training
          </button>
        </div>
      </div>
    );
  }

  if (modulesIncomplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <FiHelpCircle className="text-4xl text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Quiz Locked</h2>
          <p className="text-gray-600 mb-6 font-medium">
            You must complete all training modules before taking the quiz.
          </p>
          <button
            onClick={() => navigate(`/training/${id}`)}
            className="bg-[#3c79bb] text-white px-6 py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30"
          >
            Back to Training
          </button>
        </div>
      </div>
    );
  }

  if (awaitingGrading && !submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-8">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-6">
            <FiClock className="text-4xl text-violet-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Awaiting review</h2>
          <p className="text-gray-600 mb-6 font-medium">
            Your quiz—including written answers—is with an administrator for grading. You&apos;ll see your
            final score here once it&apos;s complete.
          </p>
          <button
            onClick={() => navigate(`/training/${id}`)}
            className="bg-[#3c79bb] text-white px-6 py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30"
          >
            Back to Training
          </button>
        </div>
      </div>
    );
  }

  const questions = quiz.questions || [];
  const scorePercentage = results ? results.score : null;
  const passed = results ? results.passed : false;

  return (
    <div className="pb-8">
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-5">
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate(`/training/${id}`)}
              className="p-2 hover:bg-white/50 rounded-xl transition-colors"
            >
              <FiArrowLeft className="text-xl text-gray-900" />
            </button>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{quiz.title || 'Quiz'}</h1>
          </div>
          {quiz.description && (
            <p className="text-sm sm:text-base text-gray-700 mt-1 font-medium ml-12">{quiz.description}</p>
          )}
          {!submitted && (
            <div className="mt-3 ml-12 text-sm text-gray-700 font-medium">
              Passing Score: {quiz.passing_score}% • {questions.length} Question{questions.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </header>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <main className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {submitted && results ? (
          <div className="glass-card rounded-2xl p-6 sm:p-8">
            {results.pending_review ? (
              <div className="text-center mb-8 p-6 rounded-2xl bg-violet-50 border-2 border-violet-200">
                <FiClock className="text-6xl text-violet-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2 text-gray-900">Submitted for grading</h2>
                <p className="text-gray-700 font-medium mb-2">
                  Your written responses will be reviewed by an administrator. You&apos;ll receive your
                  final score when grading is complete.
                </p>
                {results.mc_total > 0 && results.mc_score_preview != null && (
                  <p className="text-sm text-gray-600">
                    Multiple choice (unofficial until graded): {results.mc_correct} / {results.mc_total} correct (
                    {results.mc_score_preview}%)
                  </p>
                )}
              </div>
            ) : (
              <>
                <div
                  className={`text-center mb-8 p-6 rounded-2xl ${
                    passed ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
                  }`}
                >
                  {passed ? (
                    <FiCheckCircle className="text-6xl text-green-600 mx-auto mb-4" />
                  ) : (
                    <FiXCircle className="text-6xl text-red-600 mx-auto mb-4" />
                  )}
                  <h2 className="text-3xl font-bold mb-2">
                    {passed ? 'Congratulations! You Passed!' : 'Quiz Not Passed'}
                  </h2>
                  <div className="text-4xl font-bold mb-2">Score: {scorePercentage}%</div>
                  <p className="text-lg font-medium">
                    {results.correct} out of {results.total} questions correct
                  </p>
                  <p className="text-sm mt-2">Passing score required: {quiz.passing_score}%</p>
                </div>
              </>
            )}

            {/* Detailed Results */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Question Review</h3>
              {questions.map((question, index) => {
                const userAnswer = answers[question.id];
                const result = results.results[question.id];
                const isWriteIn = questionIsWriteIn(question);

                if (isWriteIn) {
                  const pending = result?.pending_review;
                  return (
                    <div
                      key={question.id}
                      className={`p-5 rounded-xl border-2 ${
                        pending ? 'bg-violet-50 border-violet-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {pending ? (
                          <FiClock className="text-2xl text-violet-600 flex-shrink-0 mt-1" />
                        ) : result?.correct ? (
                          <FiCheckCircle className="text-2xl text-green-600 flex-shrink-0 mt-1" />
                        ) : (
                          <FiXCircle className="text-2xl text-red-600 flex-shrink-0 mt-1" />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 mb-2">
                            Question {index + 1} (written): {question.question}
                          </h4>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap bg-white/80 rounded-lg p-3 border border-gray-200">
                            {typeof userAnswer === 'string' ? userAnswer : ''}
                          </p>
                          {pending && (
                            <p className="text-sm text-violet-800 font-medium mt-2">
                              Pending administrator review
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                const isCorrect = result.correct;
                return (
                  <div
                    key={question.id}
                    className={`p-5 rounded-xl border-2 ${
                      isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      {isCorrect ? (
                        <FiCheckCircle className="text-2xl text-green-600 flex-shrink-0 mt-1" />
                      ) : (
                        <FiXCircle className="text-2xl text-red-600 flex-shrink-0 mt-1" />
                      )}
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-3">
                          Question {index + 1}: {question.question}
                        </h4>
                        <div className="space-y-2">
                          {(question.options || []).map((option, optIndex) => {
                            const isUserAnswer = userAnswer === optIndex;
                            const isCorrectAnswer =
                              typeof result?.correctAnswer === 'number'
                                ? optIndex === result.correctAnswer
                                : optIndex === question.correct_answer;

                            return (
                              <div
                                key={optIndex}
                                className={`p-3 rounded-lg ${
                                  isCorrectAnswer
                                    ? 'bg-green-100 border-2 border-green-500 font-semibold'
                                    : isUserAnswer && !isCorrect
                                    ? 'bg-red-100 border-2 border-red-500'
                                    : 'bg-white border border-gray-300'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isCorrectAnswer && <FiCheck className="text-green-600" />}
                                  {isUserAnswer && !isCorrect && <FiXCircle className="text-red-600" />}
                                  <span>{option}</span>
                                  {isCorrectAnswer && (
                                    <span className="ml-auto text-sm font-semibold text-green-700">
                                      Correct Answer
                                    </span>
                                  )}
                                  {isUserAnswer && !isCorrect && (
                                    <span className="ml-auto text-sm font-semibold text-red-700">
                                      Your Answer
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Training Completed Banner */}
            {trainingCompletion && passed && !results.pending_review && (
              <div className="mb-6 p-5 rounded-2xl border-2 border-green-300 bg-green-50">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <FiAward className="text-2xl text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-green-900">Training Completed!</h3>
                      <p className="text-sm text-green-700 font-medium">
                        Certificate ID: {trainingCompletion.certificate_id}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCertificate(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-semibold text-sm shadow-lg shadow-green-600/30"
                  >
                    <FiAward className="text-base" />
                    View Certificate
                  </button>
                </div>
              </div>
            )}

            <div className="mt-8 flex gap-4">
              <button
                onClick={() => navigate(`/training/${id}`)}
                className="flex-1 bg-[#3c79bb] text-white px-6 py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30"
              >
                Back to Training
              </button>
              {!passed && !results.pending_review && (
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setAnswers({});
                    setResults(null);
                  }}
                  className="flex-1 bg-gray-100 text-gray-900 px-6 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold border border-gray-300"
                >
                  Retake Quiz
                </button>
              )}
              {results.pending_review && (
                <button
                  onClick={() => navigate(`/training/${id}`)}
                  className="flex-1 bg-violet-100 text-violet-900 px-6 py-3 rounded-xl hover:bg-violet-200 transition-all duration-200 font-semibold border border-violet-300"
                >
                  Back to Training
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-6 sm:p-8">
            <div className="space-y-6">
              {questions.length === 0 ? (
                <p className="text-gray-700 text-center py-8">No questions available for this quiz.</p>
              ) : (
                questions.map((question, index) => (
                  <div key={question.id} className="pb-6 border-b border-white/20 last:border-0">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Question {index + 1}: {question.question}
                      {questionIsWriteIn(question) && (
                        <span className="ml-2 text-sm font-normal text-violet-700">(written response)</span>
                      )}
                    </h3>
                    {questionIsWriteIn(question) ? (
                      <textarea
                        value={typeof answers[question.id] === 'string' ? answers[question.id] : ''}
                        onChange={(e) => handleWriteInChange(question.id, e.target.value)}
                        rows={5}
                        placeholder="Type your answer here…"
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white/80 text-gray-900 font-medium focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb]"
                      />
                    ) : (
                      <div className="space-y-2">
                        {(question.options || []).map((option, optIndex) => (
                          <label
                            key={optIndex}
                            className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                              answers[question.id] === optIndex
                                ? 'bg-[#3c79bb]/20 border-2 border-[#3c79bb]'
                                : 'bg-white/50 border border-gray-300 hover:bg-white/80'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={optIndex}
                              checked={answers[question.id] === optIndex}
                              onChange={() => handleAnswerChange(question.id, optIndex)}
                              className="w-5 h-5 text-[#3c79bb] focus:ring-[#3c79bb]"
                            />
                            <span className="flex-1 text-gray-900 font-medium">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="mt-8 pt-6 border-t border-white/20">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">
                  {questions.filter((q) => isQuestionAnswered(q)).length} of {questions.length} questions
                  answered
                </span>
                <button
                  type="submit"
                  disabled={
                    submitting ||
                    questions.length === 0 ||
                    questions.some((q) => !isQuestionAnswered(q))
                  }
                  className="bg-[#3c79bb] text-white px-8 py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#3c79bb]"
                >
                  {submitting ? 'Submitting...' : 'Submit Quiz'}
                </button>
              </div>
            </div>
          </form>
        )}
      </main>

      {/* Certificate Modal */}
      {showCertificate && trainingCompletion && (
        <CertificateModal
          completion={trainingCompletion}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );
}

export default TrainingQuiz;
